/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';

import {
    AccountProgress,
    AccountStatus,
    ApiKeyStatus,
    Country,
    InvoiceUploadRule,
    UserRight,
    UserRole
} from '../util/interfaces';

import {generateUUID, IndexedObject, ModelResponse, nullify, ResponseData, undefine} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import User from './userModel';

import PDFTemplate from './pdfTemplateModel';

import {
    createAccount,
    deleteAccount,
    deleteAdminAccount,
    getAccount,
    getNumImporting,
    pingAccountStatus,
    updateAccount,
    updateAdminAccount
} from '../util/requestSender';

import Invoice from './invoiceModel';

import Settings from './settingsModel';

import BolProduct from './bolProductModel';

import Subscription from './subscriptionModel';

import ApiKey from './apiKeyModel';

import EmailStep from './emailStepModel';

import {deepClone} from '../util/utils';

interface Props extends BaseProps {
    company_name?: string;
    address?: string;
    zip_code?: string;
    city?: string;
    country?: Country;
    coc_number?: string;
    vat_number?: string;
    iban?: string;
    logo?: string;
    email?: string;
    tel_number?: string;
    user_id?: string;
    user?: User | any;
    template?: PDFTemplate | any;
    invoices?: (Invoice | any)[];
    settings?: Settings | any;
    status?: AccountStatus;
    error?: string;
    num_invoices?: number;
    products?: (BolProduct | any)[];
    subscription?: Subscription | any | null;
    api_keys?: (ApiKey | any)[];
    email_steps?: (EmailStep | any)[];
    vat_numbers?: IndexedObject<string>;
    flex?: IndexedObject<any>;
    platform_label?: string;
    does_auto_send?: boolean;
    history_imported?: boolean;
    user_role?: UserRole;
    user_rights?: Array<UserRight>;
}

export interface AccountFields extends BaseFields {
    company_name: string;
    address: string;
    zip_code: string;
    city: string;
    country: Country;
    coc_number: string;
    vat_number: string;
    iban: string;
    logo: string;
    email: string;
    tel_number: string;
    user_id: string;
    user: User | null;
    template: PDFTemplate | null;
    invoices: Invoice[];
    settings: Settings | null;
    status: AccountStatus;
    status_last_checked: string;
    error: string;
    num_invoices: number;
    products: BolProduct[];
    subscription: Subscription | null;
    api_keys: ApiKey[];
    email_steps: EmailStep[];
    does_auto_send: boolean;
    vat_numbers: IndexedObject<string>;
    flex: IndexedObject<any>;
    platform_label: string;
    history_imported: boolean;
    user_role: UserRole;
    user_rights: Array<UserRight>;
}

const fields: AccountFields = {
    id: '',
    created_at: '',
    updated_at: '',
    company_name: '',
    address: '',
    zip_code: '',
    city: '',
    country: Country.NL,
    coc_number: '',
    vat_number: '',
    iban: '',
    logo: '',
    email: '',
    tel_number: '',
    user_id: '',
    user: null,
    template: null,
    invoices: new Array<Invoice>(),
    settings: null,
    status: AccountStatus.IDLE,
    status_last_checked: '',
    error: '',
    num_invoices: 0,
    products: [],
    subscription: null,
    api_keys: new Array<ApiKey>(),
    email_steps: new Array<EmailStep>(),
    does_auto_send: false,
    vat_numbers: {},
    flex: {},
    platform_label: '',
    history_imported: false,
    user_role: UserRole.ACCOUNTANT,
    user_rights: []
};

type FieldDef = {
    [key in keyof Omit<
        AccountFields,
        'user' | 'template' | 'invoices' | 'settings' | 'products' | 'subscription' | 'api_keys' | 'email_steps'
    >]: FieldDefinition;
};
const fieldsDefinition: FieldDef = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    company_name: createFieldDef(ModelFieldType.STRING),
    address: createFieldDef(ModelFieldType.STRING),
    zip_code: createFieldDef(ModelFieldType.STRING),
    city: createFieldDef(ModelFieldType.STRING),
    country: createFieldDef(ModelFieldType.STRING, Country.NL),
    coc_number: createFieldDef(ModelFieldType.STRING),
    vat_number: createFieldDef(ModelFieldType.STRING),
    iban: createFieldDef(ModelFieldType.STRING),
    logo: createFieldDef(ModelFieldType.STRING),
    user_id: createFieldDef(ModelFieldType.STRING),
    tel_number: createFieldDef(ModelFieldType.STRING),
    email: createFieldDef(ModelFieldType.STRING),
    status: createFieldDef(ModelFieldType.STRING, AccountStatus.IDLE),
    status_last_checked: createFieldDef(ModelFieldType.STRING),
    error: createFieldDef(ModelFieldType.STRING),
    num_invoices: createFieldDef(ModelFieldType.NUMBER, 0),
    does_auto_send: createFieldDef(ModelFieldType.BOOLEAN),
    vat_numbers: createFieldDef(ModelFieldType.OBJECT),
    flex: createFieldDef(ModelFieldType.OBJECT),
    platform_label: createFieldDef(ModelFieldType.STRING),
    history_imported: createFieldDef(ModelFieldType.BOOLEAN),
    user_role: createFieldDef(ModelFieldType.STRING, UserRole.ACCOUNTANT),
    user_rights: createFieldDef(ModelFieldType.ARRAY)
};

const ignoreProps: Array<keyof Props> = [
    'user',
    'template',
    'invoices',
    'settings',
    'products',
    'subscription',
    'api_keys',
    'email_steps'
];

export type ImportingStat = {products: number; importing: boolean};
export default class Account extends BaseModel<Props, AccountFields> {
    static ORDER_FIELDS_FLEX = 'export_order_fields';
    static INVOICE_FIELDS_FLEX = 'export_invoice_fields';

    static get = (account_id: string, callback: ModelResponse<Account | null>) => {
        getAccount({
            account_id,
            callback: (data: ResponseData) => {
                const account: Account | null = data.success ? new Account(data.account) : null;
                callback(account, data.success, data);
            }
        });
    };

    static fromUser = (user: User): Account =>
        new Account({
            address: user.address,
            zip_code: user.zipCode,
            city: user.city,
            country: user.country,
            tel_number: user.telNumber,
            email: user.email,
            company_name: user.flex.companyName || '',
            user,
            products: [],
            api_keys: []
        });

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get companyName(): string {
        return this.fields.company_name;
    }

    set companyName(company_name: string) {
        this.fields.company_name = company_name;
    }

    get address(): string {
        return this.fields.address;
    }

    set address(address: string) {
        this.fields.address = address;
    }

    get zipCode(): string {
        return this.fields.zip_code;
    }

    set zipCode(zipCode: string) {
        this.fields.zip_code = zipCode;
    }

    get city(): string {
        return this.fields.city;
    }

    set city(city: string) {
        this.fields.city = city;
    }

    get country(): Country {
        return this.fields.country;
    }

    set country(country: Country) {
        this.fields.country = country;
    }

    get cocNumber(): string {
        return this.fields.coc_number;
    }

    set cocNumber(coc_number: string) {
        this.fields.coc_number = coc_number;
    }

    get vatNumber(): string {
        return this.fields.vat_number;
    }

    set vatNumber(vat_number: string) {
        this.fields.vat_number = vat_number;
    }

    get iban(): string {
        return this.fields.iban;
    }

    set iban(iban: string) {
        this.fields.iban = iban;
    }

    get logo(): string {
        return this.fields.logo;
    }

    set logo(logo: string) {
        this.fields.logo = logo;
    }

    get email(): string {
        return this.fields.email;
    }

    set email(email: string) {
        this.fields.email = email;
    }

    get telNumber(): string {
        return this.fields.tel_number;
    }

    set telNumber(number: string) {
        this.fields.tel_number = number;
    }

    get status(): AccountStatus {
        return this.fields.status;
    }

    set status(status: AccountStatus) {
        this.fields.status = status;
    }

    get statusLastChecked(): string {
        return this.fields.status_last_checked;
    }

    get statusLastCheckedMoment(): IDate {
        return new IDate(this.statusLastChecked);
    }

    set statusLastChecked(lastChecked: string) {
        this.fields.status_last_checked = lastChecked;
    }

    set statusLastCheckedMoment(lastChecked: IDate) {
        this.statusLastChecked = lastChecked.format();
    }

    get translatedStatus(): string {
        let status: string;
        switch (this.status) {
            case AccountStatus.ERROR:
                status = this.error;
                break;
            case AccountStatus.IDLE:
                status = 'bijgewerkt';
                break;
            case AccountStatus.IMPORTING:
                status = 'facturen importeren';
                break;
            case AccountStatus.SETUP_API:
                return 'api instellen';
        }
        if (this.doesAutoSend) status += ' (Automatisch verzenden)';
        return status;
    }

    get userId(): string {
        return this.fields.user_id;
    }

    set userId(user_id: string) {
        this.fields.user_id = user_id;
    }

    get user(): User | null {
        return this.fields.user;
    }

    set user(user: User | null) {
        this.fields.user = user;
        if (user) this.userId = user.id;
    }

    get template(): PDFTemplate | null {
        return this.fields.template;
    }

    set template(template: PDFTemplate | null) {
        this.fields.template = template;
    }

    get invoices(): Invoice[] {
        return this.fields.invoices;
    }

    set invoices(invoices: Invoice[]) {
        this.fields.invoices = invoices;
    }

    get error(): string {
        return this.fields.error;
    }

    set error(error: string) {
        this.fields.error = error;
    }

    get numInvoices(): number {
        return this.fields.num_invoices;
    }

    get products(): BolProduct[] {
        return this.fields.products;
    }

    set products(products: BolProduct[]) {
        this.fields.products = products;
    }

    get subscription(): Subscription | null {
        return this.fields.subscription;
    }

    set subscription(subscription: Subscription | null) {
        this.fields.subscription = subscription;
    }

    get apiKeys(): ApiKey[] {
        return this.fields.api_keys;
    }

    set apiKeys(apiKeys: ApiKey[]) {
        this.fields.api_keys = apiKeys;
    }

    get emailSteps(): EmailStep[] {
        return this.fields.email_steps;
    }

    set emailSteps(emailSteps: EmailStep[]) {
        this.fields.email_steps = emailSteps;
    }

    get doesAutoSend(): boolean {
        return this.fields.does_auto_send;
    }

    set doesAutoSend(doesAutoSend: boolean) {
        this.fields.does_auto_send = doesAutoSend;
    }

    get vatNumbers(): IndexedObject<string> {
        return this.fields.vat_numbers;
    }

    set vatNumbers(vat_numbers: IndexedObject<string>) {
        this.fields.vat_numbers = vat_numbers;
    }

    getVatNumber = (countryCode: string): string => {
        return this.vatNumbers[countryCode] ?? this.vatNumber;
    };

    get flex(): IndexedObject<any> {
        return this.fields.flex;
    }

    set flex(flex: IndexedObject<any>) {
        this.fields.flex = flex;
    }

    get platformLabel(): string {
        return this.fields.platform_label;
    }

    set platformLabel(label: string) {
        this.fields.platform_label = label;
    }

    get selectablePlatformLabel(): string {
        const {platformLabel, companyName} = this;
        if (!platformLabel.length) return companyName;
        return platformLabel;
    }

    get labelWithSellerID(): string {
        const {selectablePlatformLabel, sellerID} = this;
        if (sellerID) return `${selectablePlatformLabel} (${sellerID})`;
        return selectablePlatformLabel;
    }

    get historyImported(): boolean {
        return this.fields.history_imported;
    }

    set historyImported(value: boolean) {
        this.fields.history_imported = value;
    }

    get sellerID(): string | null {
        const apiKey = this.getAPIKey();
        return apiKey?.sellerID ?? null;
    }

    get userRole(): UserRole {
        return this.fields.user_role;
    }

    set userRole(role: UserRole) {
        this.fields.user_role = role;
    }

    get userRights(): Array<UserRight> {
        return this.fields.user_rights;
    }

    set userRights(rights: Array<UserRight>) {
        this.fields.user_rights = rights;
    }

    clone = (deep: boolean = true): Account => {
        let fields = this.fields;
        if (deep) fields = deepClone(fields);
        return new Account(fields);
    };

    get settings(): Settings {
        return this.fields.settings!;
    }

    set settings(settings: Settings) {
        this.fields.settings = settings;
    }

    get latestInvoiceDate(): IDate | null {
        let latestDate: IDate | null = null;
        for (const invoice of this.invoices) {
            const date = invoice.momentDate;
            if (!latestDate || date.isAfter(latestDate)) latestDate = date;
        }
        return latestDate;
    }

    get setupProgress(): AccountProgress {
        return (
            this.getFlex('account_setup_progress') ?? {
                api: false,
                mail: false,
                vat: false,
                pdf_layout: false,
                upload: false
            }
        );
    }

    set setupProgress(progress: AccountProgress) {
        this.setFlex('account_setup_progress', progress);
    }

    isAccountOwner = (): boolean => {
        return this.fields.user_role === UserRole.OWNER;
    };

    getAPIKey = (): ApiKey | null => {
        if (!this.apiKeys.length) return null;
        return this.apiKeys[0];
    };

    getActiveAPIKey = (): ApiKey | null => {
        if (!this.apiKeys.length) return null;
        return this.apiKeys.find((akey: ApiKey) => akey.isActive) ?? null;
    };

    addInvoice = (invoice: Invoice): Account => {
        for (const inv of this.invoices) if (inv.id === invoice.id) return this.updateInvoice(invoice);
        this.invoices = this.invoices.concat(invoice);
        return this;
    };

    updateInvoice = (invoice: Invoice): Account => this.removeInvoice(invoice).addInvoice(invoice);

    removeInvoice = (invoice: Invoice): Account => {
        this.invoices = this.invoices.filter((inv: Invoice) => inv.id !== invoice.id);
        return this;
    };

    addApiKey = (apiKey: ApiKey): Account => {
        this.apiKeys = this.apiKeys.concat(apiKey);
        return this;
    };

    replaceApiKey = (apiKey: ApiKey): Account => {
        return this.removeApiKey(apiKey).addApiKey(apiKey);
    };

    removeApiKey = (apiKey: ApiKey): Account => {
        this.apiKeys = this.apiKeys.filter((key: ApiKey) => key.id !== apiKey.id);
        return this;
    };

    setFlex = (key: string, value: any) => {
        const flex = this.flex ?? {};
        flex[key] = value;
        this.flex = flex;
    };

    getFlex = (key: string): any => {
        return (this.flex ?? {})[key];
    };

    save = (
        clientId: string | undefined,
        clientSecret: string | undefined,
        logo: File | null,
        callback: ModelResponse<Account>
    ) => {
        createAccount({
            data: {
                address: nullify(this.address),
                city: nullify(this.city),
                country: nullify(this.country),
                client_id: nullify(clientId),
                client_secret: nullify(clientSecret),
                coc_number: nullify(this.cocNumber),
                company_name: this.companyName,
                vat_number: nullify(this.vatNumber),
                zip_code: nullify(this.zipCode),
                email: this.email,
                invoice_prefix: undefine(this.settings.invoicePrefix),
                invoice_start: undefine(this.settings.invoiceStart),
                reply_email: nullify(this.settings.replyEmail) || this.email,
                tel_number: nullify(this.telNumber),
                use_bol_text: this.settings.useBolText,
                use_platform_text: this.settings.useBolText,
                email_template: this.settings.emailTemplate,
                email_subject: nullify(this.settings.emailSubject),
                email_bcc: nullify(this.settings.emailBcc),
                iban: nullify(this.iban),
                sender_name: nullify(this.settings.senderName),
                send_canceled_invoices: this.settings.sendCanceledInvoices,
                vat_numbers: this.vatNumbers,
                flex: this.flex,
                platform_label: nullify(this.platformLabel),
                logo,
                auto_respond_old_orders: this.settings.autoRespondOldOrders,
                old_order_invoice_prefix: this.settings.oldOrderInvoicePrefix,
                old_order_invoice_start: this.settings.oldOrderInvoiceStart
            },
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.account);
                callback(this, data.success, data);
            }
        });
    };

    update = (clientId?: string, clientSecret?: string, logo?: File | null, callback?: ModelResponse<Account>) => {
        updateAccount({
            data: {
                account_id: this.id,
                address: nullify(this.address),
                city: nullify(this.city),
                country: nullify(this.country),
                client_id: clientId,
                client_secret: clientSecret,
                coc_number: nullify(this.cocNumber),
                company_name: this.companyName,
                logo,
                vat_number: nullify(this.vatNumber),
                zip_code: nullify(this.zipCode),
                use_platform_text: this.settings.usePlatformText,
                use_bol_text: this.settings.useBolText,
                tel_number: nullify(this.telNumber),
                reply_email: nullify(this.settings.replyEmail),
                invoice_start: undefine(this.settings.invoiceStart),
                invoice_prefix: undefine(this.settings.invoicePrefix),
                email: undefine(this.email),
                email_template: this.settings.emailTemplate,
                email_subject: nullify(this.settings.emailSubject),
                email_bcc: nullify(this.settings.emailBcc),
                iban: nullify(this.iban),
                sender_name: nullify(this.settings.senderName),
                send_canceled_invoices: this.settings.sendCanceledInvoices,
                vat_numbers: this.vatNumbers,
                flex: this.flex,
                platform_label: nullify(this.platformLabel),
                email_max_product_name_length: this.settings.emailMaxProductNameLength,
                reverse_vat_countries: nullify(this.settings.reverseVatCountries),
                filename_base: this.settings.filenameBase,
                auto_respond_invoice_requests: this.settings.autoRespondInvoiceRequests,
                proactively_upload_invoices: this.settings.proactivelyUploadInvoices,
                invoice_upload_rules: this.settings.invoiceUploadRules,
                notify_missing_invoice: this.settings.notifyMissingInvoice,
                notify_missing_invoice_mail: nullify(this.settings.notifyMissingInvoiceMail),
                include_ubl_attachment: this.settings.includeUBLAttachment,
                auto_respond_old_orders: this.settings.autoRespondOldOrders,
                old_order_invoice_prefix: this.settings.oldOrderInvoicePrefix,
                old_order_invoice_start: this.settings.oldOrderInvoiceStart
            },
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.account);
                if (callback) callback(this, data.success, data);
            }
        });
    };

    updateAdmin = (callback: ModelResponse<Account>) => {
        const data = {
            account_id: this.id,
            address: nullify(this.address),
            city: nullify(this.city),
            country: nullify(this.country),
            coc_number: this.cocNumber,
            company_name: this.companyName,
            vat_number: nullify(this.vatNumber),
            zip_code: nullify(this.zipCode),
            use_platform_text: this.settings.usePlatformText,
            use_bol_text: this.settings.useBolText,
            tel_number: nullify(this.telNumber),
            reply_email: nullify(this.settings.replyEmail),
            invoice_start: undefine(this.settings.invoiceStart),
            invoice_prefix: undefine(this.settings.invoicePrefix),
            email: undefine(this.email),
            email_template: this.settings.emailTemplate,
            email_subject: nullify(this.settings.emailSubject),
            email_bcc: nullify(this.settings.emailBcc),
            iban: nullify(this.iban),
            sender_name: nullify(this.settings.senderName),
            vat_numbers: this.vatNumbers,
            flex: this.flex,
            platform_label: nullify(this.platformLabel),
            email_max_product_name_length: this.settings.emailMaxProductNameLength,
            reverse_vat_countries: nullify(this.settings.reverseVatCountries),
            filename_base: this.settings.filenameBase,
            auto_respond_invoice_requests: this.settings.autoRespondInvoiceRequests,
            proactively_upload_invoices: this.settings.proactivelyUploadInvoices,
            invoice_upload_rules: this.settings.invoiceUploadRules,
            notify_missing_invoice: this.settings.notifyMissingInvoice,
            notify_missing_invoice_mail: nullify(this.settings.notifyMissingInvoiceMail),
            include_ubl_attachment: this.settings.includeUBLAttachment,
            auto_respond_old_orders: this.settings.autoRespondOldOrders,
            old_order_invoice_prefix: this.settings.oldOrderInvoicePrefix,
            old_order_invoice_start: this.settings.oldOrderInvoiceStart,
            participate_kor: this.settings.participateKOR
        };
        updateAdminAccount({
            data,
            id: this.userId,
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.account);
                callback(this, data.success, data);
            }
        });
    };

    delete = (callback: ModelResponse<Account>) => {
        deleteAccount({
            id: this.id,
            callback: (data: ResponseData) => {
                callback(this, data.success, data);
            }
        });
    };

    deleteAdmin = (callback: ModelResponse<Account>) => {
        deleteAdminAccount({
            account_id: this.id,
            user_id: this.userId,
            callback: (data: ResponseData) => {
                callback(this, data.success, data);
            }
        });
    };

    getImportingProductsCount = (): Promise<ImportingStat> =>
        new Promise<ImportingStat>((resolve, reject) => {
            getNumImporting({
                account_id: this.id,
                callback: (data: ResponseData) => {
                    if (data.success) resolve({products: data.num_importing, importing: data.is_importing});
                    else reject(data.message);
                }
            });
        });

    pingStatus = (callback: ModelResponse<Account>) => {
        return pingAccountStatus({
            id: this.id,
            callback: (data: ResponseData) => {
                if (data.success) {
                    this.status = data.account_status;
                    this.error = data.error;
                    this.statusLastCheckedMoment = IDate.now();
                }
                callback(this, data.success, data);
            }
        });
    };

    getInvoiceUploadRules = (): Array<InvoiceUploadRule> => {
        const rules = this.settings?.invoiceUploadRules ?? [];
        return rules.map((rule: InvoiceUploadRule) => {
            rule.id = generateUUID();
            return rule;
        });
    };

    hasActiveAPIKey = (): boolean => {
        if (!this.fields.api_keys) return false;
        return !!this.fields.api_keys.find((apiKey: ApiKey) =>
            [ApiKeyStatus.ACTIVE, ApiKeyStatus.IMPORTING].includes(apiKey.status)
        );
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (props.user) {
                this.user = props.user instanceof User ? props.user : new User(props.user);
            }
            if (props.template) {
                const template: PDFTemplate =
                    props.template instanceof PDFTemplate ? props.template : new PDFTemplate(props.template);
                template.account = this;
                this.template = template;
            }
            if (props.invoices) {
                const invoices: Invoice[] = [];
                for (const inv of props.invoices) {
                    const invoice = inv instanceof Invoice ? inv : new Invoice(inv);
                    invoice.account = this;
                    invoices.push(invoice);
                }
                this.invoices = invoices;
            }
            if (props.settings) {
                this.settings =
                    props.settings instanceof Settings
                        ? props.settings
                        : new Settings({...props.settings, account: this});
            } else this.settings = new Settings({account: this});
            if (props.products) {
                const products: BolProduct[] = [];
                for (const prod of props.products) {
                    const product = prod instanceof BolProduct ? prod : new BolProduct(prod);
                    product.account = this;
                    products.push(product);
                }
                this.fields.products = products;
            }
            if (props.subscription) {
                this.subscription =
                    props.subscription instanceof Subscription
                        ? props.subscription
                        : new Subscription({
                              ...props.subscription,
                              account: this
                          });
            }
            if (props.api_keys) {
                const apiKeys: ApiKey[] = [];
                for (const key of props.api_keys) {
                    const apiKey: ApiKey = key instanceof ApiKey ? key : new ApiKey(key);
                    apiKey.account = this;
                    apiKeys.push(apiKey);
                }
                this.fields.api_keys = apiKeys;
            }
            if (props.email_steps) {
                const emailSteps: EmailStep[] = [];
                for (const stp of props.email_steps) {
                    const step: EmailStep = stp instanceof EmailStep ? stp : new EmailStep(stp);
                    step.account = this;
                    emailSteps.push(step);
                }
                this.fields.email_steps = emailSteps;
            }
        }
    };
}
